import { avatarAppConfig } from "../Components/Avatar/config";

/**
 * Universal High-Fidelity Voice Synthesis Service
 * Primary: Microsoft Azure Neural US English (en-US-AvaMultilingualNeural / en-US-JennyNeural)
 * Secondary: Sarvam AI Multilingual (bulbul:v3)
 * Fallback: Web Speech API (en-US)
 */
class HighDefinitionTtsService {
  constructor() {
    this.currentAudio = null;
    this.abortController = null;
    this.isSpeaking = false;
    this.isPlayingAudio = false;
    this.listeners = new Set();
    this.audioCache = new Map(); // In-memory cache for instantaneous playback
    this.queue = []; // Sentence completion queue
    this.currentText = "";
  }

  getAzureConfig() {
    return {
      key:
        process.env.REACT_APP_AZURE_SPEECH_KEY ||
        avatarAppConfig?.cogSvcSubKey ||
        "1IiMC3dgY83tkF1MRHtATL3L4UVxbJ5g0MT4dWXX6GZpPO9QbJv0JQQJ99CEACqBBLyXJ3w3AAAYACOGIXoL",
      region:
        process.env.REACT_APP_AZURE_SPEECH_REGION ||
        avatarAppConfig?.cogSvcRegion ||
        "southeastasia"
    };
  }

  getSarvamKey() {
    return (
      process.env.REACT_APP_SARVAM_API_KEY ||
      avatarAppConfig?.sarvamAPIKey ||
      "sk_0k69tgy2_AVfNH0yTxm9HywkCfqd3CHAk"
    );
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notify(speakingState) {
    this.isSpeaking = speakingState;
    this.listeners.forEach((fn) => {
      try {
        fn(speakingState);
      } catch (_) {}
    });
  }

  cleanTextForSpeech(text) {
    if (!text) return "";
    return text
      .replace(
        /[\u{1F600}-\u{1F6FF}|\u{2600}-\u{26FF}|\u{2700}-\u{27BF}|\u{1F300}-\u{1F5FF}|\u{1F900}-\u{1F9FF}|\u{1F018}-\u{1F270}|\u{2388}-\u{2454}]/gu,
        ""
      )
      .replace(/[•*#_~`]/g, "")
      .replace(/https?:\/\/\S+/g, "")
      .replace(/&/g, " and ")
      .replace(/</g, " ")
      .replace(/>/g, " ")
      .replace(/\n+/g, ". ")
      .replace(/\s+/g, " ")
      .trim();
  }

  stop() {
    this.queue = []; // Clear pending queue on explicit stop
    this.currentText = "";
    this.isPlayingAudio = false;

    if (this.abortController) {
      try {
        this.abortController.abort();
      } catch (_) {}
      this.abortController = null;
    }

    if (this.currentAudio) {
      try {
        this.currentAudio.pause();
        this.currentAudio.currentTime = 0;
        this.currentAudio.src = "";
      } catch (_) {}
      this.currentAudio = null;
    }

    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      try {
        window.speechSynthesis.cancel();
      } catch (_) {}
    }

    this.notify(false);
  }

  async speak(rawText, options = {}) {
    const cleanText = this.cleanTextForSpeech(rawText);
    if (!cleanText) return;

    // If explicit interrupt is requested, clear queue & stop immediately
    if (options.interrupt) {
      this.stop();
    }

    // Prevent duplicate enqueue of the exact same sentence
    if (this.currentText === cleanText || this.queue.some(item => item.cleanText === cleanText)) {
      return;
    }

    // Enqueue sentence to ensure complete playback without truncation
    this.queue.push({ cleanText, rawText, options });

    if (!this.isPlayingAudio) {
      this.processQueue();
    }
  }

  async processQueue() {
    if (this.queue.length === 0) {
      this.isPlayingAudio = false;
      this.currentText = "";
      this.notify(false);
      return;
    }

    const nextItem = this.queue.shift();
    const { cleanText, options } = nextItem;
    const {
      voice = "en-US-AvaMultilingualNeural",
      locale = "en-US",
      onStart,
      onEnd,
      onError
    } = options;

    this.isPlayingAudio = true;
    this.currentText = cleanText;
    this.abortController = new AbortController();

    const cacheKey = `azure-${voice}-${cleanText}`;

    const handleSentenceFinished = () => {
      this.isPlayingAudio = false;
      this.currentText = "";
      if (onEnd) onEnd();
      // Process next queued sentence after current sentence completes!
      this.processQueue();
    };

    const handleSentenceError = () => {
      this.isPlayingAudio = false;
      this.currentText = "";
      if (onError) onError();
      this.processQueue();
    };

    // 1. Check in-memory audio cache for zero-latency instant playback
    if (this.audioCache.has(cacheKey)) {
      const cachedSrc = this.audioCache.get(cacheKey);
      this.playAudioSource(cachedSrc, onStart, handleSentenceFinished, handleSentenceError);
      return;
    }

    // 2. Synthesize using Azure Neural US English Voice
    try {
      this.notify(true);
      if (onStart) onStart();

      const { key, region } = this.getAzureConfig();
      const ssml = `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="${locale}"><voice name="${voice}">${cleanText}</voice></speak>`;

      const response = await fetch(
        `https://${region}.tts.speech.microsoft.com/cognitiveservices/v1`,
        {
          method: "POST",
          headers: {
            "Ocp-Apim-Subscription-Key": key,
            "Content-Type": "application/ssml+xml",
            "X-Microsoft-OutputFormat": "audio-24khz-48kbitrate-mono-mp3",
            "User-Agent": "SaleSenseAI"
          },
          body: ssml,
          signal: this.abortController.signal
        }
      );

      if (!response.ok) {
        throw new Error(`Azure Speech API returned HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const audioUrl = URL.createObjectURL(blob);

      // Cache the audio source
      this.audioCache.set(cacheKey, audioUrl);

      this.playAudioSource(audioUrl, onStart, handleSentenceFinished, handleSentenceError);
    } catch (err) {
      if (err.name === "AbortError") {
        this.processQueue();
        return;
      }
      console.warn("Azure Neural US TTS failed, attempting Sarvam fallback:", err?.message || err);
      this.speakWithSarvamFallback(cleanText, onStart, handleSentenceFinished, handleSentenceError);
    }
  }

  async speakWithSarvamFallback(cleanText, onStart, onEnd, onError) {
    try {
      const apiKey = this.getSarvamKey();
      const response = await fetch("https://api.sarvam.ai/text-to-speech", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "api-subscription-key": apiKey
        },
        body: JSON.stringify({
          inputs: [cleanText],
          target_language_code: "en-IN",
          speaker: "shreya",
          model: "bulbul:v3",
          pace: 1.0
        }),
        signal: this.abortController?.signal
      });

      if (!response.ok) throw new Error("Sarvam fallback failed");

      const data = await response.json();
      const base64Audio = data?.audios?.[0];
      if (!base64Audio) throw new Error("No audio from Sarvam");

      const audioSrc = `data:audio/wav;base64,${base64Audio}`;
      this.playAudioSource(audioSrc, onStart, onEnd, onError);
    } catch (err) {
      if (err.name === "AbortError") return;
      console.warn("Sarvam fallback failed, using Web Speech API:", err);
      this.fallbackSpeak(cleanText, onStart, onEnd, onError);
    }
  }

  playAudioSource(audioSrc, onStart, onEnd, onError) {
    try {
      const audio = new Audio(audioSrc);
      this.currentAudio = audio;

      audio.onplay = () => {
        this.notify(true);
        if (onStart) onStart();
      };

      audio.onended = () => {
        this.notify(false);
        this.currentAudio = null;
        if (onEnd) onEnd();
      };

      audio.onerror = (e) => {
        console.warn("Audio playback error:", e);
        this.notify(false);
        this.currentAudio = null;
        if (onError) onError();
      };

      audio.play().catch((err) => {
        console.warn("Audio playback prevented by browser autoplay policy:", err);
        this.notify(false);
        if (onError) onError();
      });
    } catch (err) {
      this.notify(false);
      if (onError) onError();
    }
  }

  fallbackSpeak(text, onStart, onEnd, onError) {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      this.notify(false);
      if (onError) onError();
      return;
    }

    try {
      if (window.speechSynthesis.paused) {
        window.speechSynthesis.resume();
      }
      window.speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.0;
      utterance.pitch = 1.0;

      const voices = window.speechSynthesis.getVoices() || [];
      const naturalUsVoice =
        voices.find(
          (v) =>
            (v.lang === "en-US" || v.lang === "en_US") &&
            (v.name.includes("Natural") ||
              v.name.includes("Neural") ||
              v.name.includes("Ava") ||
              v.name.includes("Jenny") ||
              v.name.includes("Aria") ||
              v.name.includes("Samantha") ||
              v.name.includes("Google US English") ||
              v.name.includes("Siri"))
        ) ||
        voices.find((v) => v.lang === "en-US" || v.lang === "en_US") ||
        voices.find((v) => v.lang && v.lang.startsWith("en"));

      if (naturalUsVoice) utterance.voice = naturalUsVoice;

      utterance.onstart = () => {
        this.notify(true);
        if (onStart) onStart();
      };
      utterance.onend = () => {
        this.notify(false);
        if (onEnd) onEnd();
      };
      utterance.onerror = () => {
        this.notify(false);
        if (onError) onError();
      };

      window.speechSynthesis.speak(utterance);
    } catch (_) {
      this.notify(false);
      if (onError) onError();
    }
  }
}

export const sarvamTts = new HighDefinitionTtsService();
export default sarvamTts;
